// ---- Status ----

export const TaskStatuses = {
    NOT_READY: 'not_ready',
    WAITING: 'waiting',
    READY: 'ready',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
} as const;
export type TaskStatus = (typeof TaskStatuses)[keyof typeof TaskStatuses];

export const ACTIVE_TASK_STATUSES = [
    TaskStatuses.READY,
    TaskStatuses.IN_PROGRESS,
] as const satisfies readonly TaskStatus[];

export const GROUP_PANEL_TASK_STATUSES = [
    ...ACTIVE_TASK_STATUSES,
    TaskStatuses.COMPLETED,
] as const satisfies readonly TaskStatus[];

// ---- Location ----

export const TaskLocations = {
    HOME: 'home', // TODO: remove post-launch
    DOCUMENTS: 'documents', // TODO: remove post-launch
    ACCOUNTING: 'accounting',
    TAXES: 'taxes', // TODO: remove post-launch
    TAXES_ITR: 'taxes_itr',
    PAYROLL: 'payroll',
} as const;

export type TaskLocation = (typeof TaskLocations)[keyof typeof TaskLocations];

// ---- Template names ----

/**
 * Backend `TaskTemplate.name` values referenced from application code.
 *
 * Only add a name here when something outside the task feed needs to reason about a specific task
 * type — e.g. another page gating on whether that task is open. Rendering a task never needs this:
 * the framework dispatches on `submit_action`, not on the template name.
 */
export const MemberTaskTemplateNames = {
    EOY_FINANCIALS_REVIEW: 'eoy_financials_review',
    EOY_CLEANUP_CHAPTER: 'eoy_cleanup_chapter',
    // The two children of the `qte_tax_payments` group card, and the two of
    // `fees_and_franchise_taxes` — the Quarterly payments dashboard marks a tab whose group still
    // has an open task. Named here rather than read off `group_id` because the dashboard has no
    // group to look up: it knows the tab, not the card.
    REVIEW_AND_PAY_QTE: 'review_and_pay_qte',
    RECORD_QTE_PAYMENT: 'record_qte_payment',
    REVIEW_AND_PAY_FEES_AND_FRANCHISE_TAXES: 'review_and_pay_fees_and_franchise_taxes',
    RECORD_FEES_AND_FRANCHISE_TAX_PAYMENT: 'record_fees_and_franchise_tax_payment',
} as const;

// ---- Product area ----

export const TaskAreas = {
    TAXES: 'taxes',
    ACCOUNTING: 'accounting',
    PAYROLL: 'payroll',
    INVOICING: 'invoicing',
    COMPLIANCE: 'compliance',
} as const;

// ---- Priority ----

export const TaskPriorities = {
    NORMAL: 2,
    HIGH: 3,
} as const;

// ---- Submit action ----

export const SubmitActionTypes = {
    REDIRECT: 'redirect',
    UPLOAD: 'upload',
    FORM: 'form',
    DOWNLOAD: 'download',
    CUSTOM: 'custom',
} as const;
type SubmitActionType = (typeof SubmitActionTypes)[keyof typeof SubmitActionTypes];

export const FieldTypes = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    NUMBER: 'number',
    CURRENCY: 'currency',
    DATE: 'date',
    DROPDOWN: 'dropdown',
    SELECT: 'select',
    MULTISELECT: 'multiselect',
} as const;
export type FieldType = (typeof FieldTypes)[keyof typeof FieldTypes];

export interface FormField {
    key: string;
    question: string;
    description?: string;
    field_type: FieldType;
    required: boolean;
    options?: string[]; // required for DROPDOWN, SELECT, MULTISELECT
}

interface SubmitActionBase {
    type: SubmitActionType;
    label: string;
    confirmation_message?: string;
}

export interface RedirectSubmitAction extends SubmitActionBase {
    type: typeof SubmitActionTypes.REDIRECT;
    url: string;
    is_external?: boolean;
}

export interface UploadSubmitAction extends SubmitActionBase {
    type: typeof SubmitActionTypes.UPLOAD;
}

export interface FormSubmitAction extends SubmitActionBase {
    type: typeof SubmitActionTypes.FORM;
}

// Member downloads a ready file; the task's context names the target document.
export interface DownloadSubmitAction extends SubmitActionBase {
    type: typeof SubmitActionTypes.DOWNLOAD;
}

export interface CustomSubmitAction extends SubmitActionBase {
    type: typeof SubmitActionTypes.CUSTOM;
    component_name: string;
    component_props?: Record<string, unknown>;
}

export type SubmitAction =
    | RedirectSubmitAction
    | UploadSubmitAction
    | FormSubmitAction
    | DownloadSubmitAction
    | CustomSubmitAction;

// ---- Context ----

export interface BaseTaskContext {
    action_description?: string;
    notes?: string;
    [key: string]: unknown; // template-specific extras (interpolation values, operator metadata)
}

export interface UploadContext extends BaseTaskContext {
    document_type_id: string;
    month?: number | null;
    year?: number | null;
    quarter?: number | null;
}

export interface FormTaskContext extends BaseTaskContext {
    fields: FormField[];
}

export interface DownloadContext extends BaseTaskContext {
    document_uuid: string;
    file_name: string;
}

// ---- Task ----

export interface SubmitActionTask {
    id: string;
    context: unknown;
    task_template: { display_name: string };
    group_id?: string | null;
}

export interface TaskTemplate {
    id: string;
    name: string;
    display_name: string;
    instructions: string;
    submit_action: SubmitAction;
    is_member_task: boolean;
    location: string[];
    area: string;
    has_custom_component: boolean;
    sla_hours?: number;
    task_labels?: string[];
}

export interface Task {
    id: string;
    status: TaskStatus;
    priority: number;
    effective_area: string;
    context: BaseTaskContext;
    task_template: TaskTemplate;
    task_type: string;
    source: string;
    task_labels: string[];
    due_date: string;
    flagged: boolean;
    is_in_sla: boolean;
    effective_sla_hours: number;
    created_at: string;
    updated_at: string;
    completed_at?: string | null;
    waiting_on?: string | null;
    // Recorded on every cancellation; the member task pages do not read it yet. Optional and a bare
    // string because this shape is also built from payloads that predate the column.
    cancellation_reason?: string | null;
    group_id: string | null;
}

// ---- Group action ----

export const GroupActionTypes = {
    EXPAND: 'expand',
    CUSTOM: 'custom',
} as const;
type GroupActionType = (typeof GroupActionTypes)[keyof typeof GroupActionTypes];

interface GroupActionBase {
    type: GroupActionType;
}

export interface ExpandGroupAction extends GroupActionBase {
    type: typeof GroupActionTypes.EXPAND;
    label?: string;
}

export interface CustomGroupAction extends GroupActionBase {
    type: typeof GroupActionTypes.CUSTOM;
    component_name: string;
    component_props?: Record<string, unknown>;
}

export type GroupAction = ExpandGroupAction | CustomGroupAction;

// ---- Task group ----

export interface TaskGroupTemplate {
    id: string;
    name: string;
    display_name: string;
    description: string;
    location: string[];
    area: string;
    group_action: GroupAction;
    has_custom_component: boolean;
}

export interface TaskGroupSummary {
    id: string;
    group_template: TaskGroupTemplate;
    context: BaseTaskContext;
    tasks_count: number;
    next_due_date: string | null;
    priority: number;
    created_at: string;
    updated_at: string;
}

// ---- Feed items ----

export const MemberTaskFeedItemTypes = {
    TASK: 'task',
    GROUP: 'group',
} as const;

export interface MemberTaskFeedTaskItem {
    item_type: typeof MemberTaskFeedItemTypes.TASK;
    task: Task;
}

export interface MemberTaskFeedGroupItem {
    item_type: typeof MemberTaskFeedItemTypes.GROUP;
    group: TaskGroupSummary;
}

export type MemberTaskFeedItem = MemberTaskFeedTaskItem | MemberTaskFeedGroupItem;
